import dayjs from 'dayjs'
import { useCallback, useMemo } from 'react'
import { StringParam, useQueryParam, withDefault } from 'use-query-params'

import type { DateRangeValue } from '@modules/Dashboard/pages/AnalyticsV2/analyticsModule/components/tools/AnalyticsDateRangeSelect/AnalyticsDateRangeSelect.types'

import type { PrecisionValue } from '../../../../analyticsModule/components/tools/AnalyticsPrecisionSelect/AnalyticsPrecisionSelect.types'
import type { AnalyticsPrecision } from '../../../../analyticsModule/types'
import { precisionOptions } from './consts'

const getAvailablePrecisionOptions = (
  dateRange: DateRangeValue,
  supportedPrecisions?: AnalyticsPrecision[],
): PrecisionValue[] => {
  const [fromDate, toDate] = dateRange.dates
  const daysDifference = dayjs(toDate).diff(dayjs(fromDate), 'day')

  let dateRangeFilteredOptions: PrecisionValue[]

  if (daysDifference === 0) {
    // For single day range (today, yesterday): hour only
    dateRangeFilteredOptions = precisionOptions.filter((option) => option.value === 'hour')
  } else if (daysDifference <= 7) {
    // For range <= 7 days: hour, day
    dateRangeFilteredOptions = precisionOptions.filter((option) =>
      ['hour', 'day'].includes(option.value),
    )
  } else if (daysDifference <= 30) {
    // For range <= 30 days: hour, day, week
    dateRangeFilteredOptions = precisionOptions.filter((option) =>
      ['hour', 'day', 'week'].includes(option.value),
    )
  } else if (daysDifference <= 90) {
    // For range > 30 days but <= 90 days: day, week, month
    dateRangeFilteredOptions = precisionOptions.filter((option) =>
      ['day', 'week', 'month'].includes(option.value),
    )
  } else if (daysDifference <= 365) {
    // For range > 90 days but <= 365 days: day, week, month, quarter
    dateRangeFilteredOptions = precisionOptions.filter((option) =>
      ['day', 'week', 'month', 'quarter'].includes(option.value),
    )
  } else {
    // For range > 365 days: day, week, month, quarter, year
    dateRangeFilteredOptions = precisionOptions.filter((option) =>
      ['day', 'week', 'month', 'quarter', 'year'].includes(option.value),
    )
  }

  // Filter by supported precisions
  return supportedPrecisions && supportedPrecisions.length > 0
    ? dateRangeFilteredOptions.filter((option) => supportedPrecisions.includes(option.value))
    : dateRangeFilteredOptions
}

export const usePrecisionParams = (
  dateRange: DateRangeValue,
  supportedPrecisions?: AnalyticsPrecision[],
) => {
  const [precisionParam, setPrecisionParam] = useQueryParam(
    'precision',
    withDefault(StringParam, undefined),
  )

  const availableOptions = useMemo(() => {
    return getAvailablePrecisionOptions(dateRange, supportedPrecisions)
  }, [dateRange, supportedPrecisions])

  const setPrecision = useCallback(
    (newPrecision: PrecisionValue) => {
      setPrecisionParam(newPrecision.value)
    },
    [setPrecisionParam],
  )

  const precision = useMemo(() => {
    // If no precision parameter, set default option from available options
    if (!precisionParam) {
      return availableOptions.find((option) => option.value === 'day') || availableOptions[0]
    }

    // Check if the precision value is valid and available for current date range
    const isValidAndAvailable = availableOptions.some((option) => option.value === precisionParam)

    if (!isValidAndAvailable) {
      // If not available, set default option from available options
      const defaultOption =
        availableOptions.find((option) => option.value === 'day') || availableOptions[0]

      setPrecision(defaultOption)

      return defaultOption
    }

    // Find the matching option
    const matchingOption = availableOptions.find((option) => option.value === precisionParam)

    if (matchingOption) {
      // If it matches a predefined option, return it
      return matchingOption
    }

    // This should never happen due to validation above, but return first available as fallback
    return availableOptions[0]
  }, [precisionParam, availableOptions, setPrecision])

  const options = useMemo((): PrecisionValue[] => {
    return availableOptions
  }, [availableOptions])

  return {
    options,
    precision,
    setPrecision,
  }
}
